import type { Ciphers } from "../api/types/Ciphers"
import type { Base64EncodedBinary } from "../types"
import {
  type EncryptedBlobBase64,
  EncryptedBlobBase64Schema,
  type SignInData,
} from "./types"
import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  fromBufferToBase64,
} from "./utils"

export interface NewUserCipherBundle {
  /**
   * The hash of the master password and masterKey. Used to signin and nothing else.
   * It is ready to be uploaded to the server without any further modification.
   */
  masterPasswordHash: Base64EncodedBinary
  /**
   * The userSymmetricKey is the "main" key that is used to encrypt/decrypt all things
   * before they go to the server.
   *
   * This is used for encrypting "my" data. Anything I plan on sharing with others
   * should NOT be encrypted with this key. (Use the profileSymmetricKey for that).
   */
  userSymmetricKey: CryptoKey
  /**
   * profileSymmetricKey is the key used to encrypt profile data that can be shared.
   */
  profileSymmetricKey: CryptoKey
  /**
   * RSA key pair for discovery and encrypted messaging.
   */
  rsaKeyPair: CryptoKeyPair
  /**
   * protectedUserSymmetricKey is the base64 encoded bytes of the user symmetric key
   * that are ready to be uploaded to the server. It is encrypted with the stretchedMasterKey.
   */
  protectedUserSymmetricKey: Base64EncodedBinary
  /**
   * protectedProfileSymmetricKey is the base64 encoded bytes of the profile symmetric key
   * that are ready to be uploaded to the server. It is encrypted with the userSymmetricKey.
   */
  protectedProfileSymmetricKey: Base64EncodedBinary
  /**
   * protectedRsaPrivateKey is the base64 encoded bytes of the RSA private key
   * that are ready to be uploaded to the server. It is encrypted with the userSymmetricKey.
   */
  protectedRsaPrivateKey: Base64EncodedBinary
  /**
   * rsaPublicKey is the base64 encoded bytes of the RSA public key
   * that are ready to be uploaded to the server. It is not encrypted.
   */
  rsaPublicKey: Base64EncodedBinary
}

/**
 * Main function: given username + password, return the bundle.
 *
 * Anything generated from the username and password can be recomputed upon login,
 * anything that is generated using a random number generator needs to be encrypted, then stored
 * on the server so we can restore the state when logging in.
 */
export async function generateBundleForNewUser(
  username: string,
  password: string,
): Promise<NewUserCipherBundle> {
  const masterPasswordHash = await getMasterPasswordHash(username, password)
  const stretchedMasterKey = await getStretchedKey(username, password)

  const userSymmetricKey = await generateUserSymmetricKey()
  const profileSymmetricKey = await generateProfileSymmetricKey()
  const rsaKeyPair = await generateRsaKeyPair()

  // Prepare the keys that will be sent to the server
  const protectedUserSymmetricKey = await encryptKeyWithKey(
    userSymmetricKey,
    stretchedMasterKey,
  )
  const protectedProfileSymmetricKey = await encryptKeyWithKey(
    profileSymmetricKey,
    userSymmetricKey,
  )
  const protectedRsaPrivateKey = await encryptKeyWithKey(
    rsaKeyPair.privateKey,
    userSymmetricKey,
    "pkcs8",
  )
  const rsaPublicKey = await exportRSAPublicKey(rsaKeyPair.publicKey)

  return {
    masterPasswordHash,
    protectedUserSymmetricKey,
    userSymmetricKey,
    profileSymmetricKey,
    protectedProfileSymmetricKey,
    rsaKeyPair,
    protectedRsaPrivateKey,
    rsaPublicKey,
  } satisfies NewUserCipherBundle
}

export interface AuthedUserCipherBundle {
  userSymmetricKey: CryptoKey
  profileSymmetricKey: CryptoKey
  rsaPrivateKey: CryptoKey
}

/**
 * Decrypt all the keys pulled down from the server and return the CryptoKeys needed
 * to interact with the app.
 */
export async function generateBundleForSignIn(
  signInData: SignInData,
  username: string,
  password: string,
): Promise<AuthedUserCipherBundle> {
  const stretchedMasterKey = await getStretchedKey(username, password)
  const userSymmetricKey = await decryptProtectedSymmetricKey(
    signInData.protectedUserSymmetricKey,
    stretchedMasterKey,
  )
  const profileSymmetricKey = await decryptProtectedSymmetricKey(
    signInData.protectedProfileSymmetricKey,
    userSymmetricKey,
  )
  const rsaPrivateKey = await decryptProtectedRSAPrivateKey(
    signInData.protectedRsaPrivateKey,
    userSymmetricKey,
  )

  return {
    userSymmetricKey,
    profileSymmetricKey,
    rsaPrivateKey,
  } satisfies AuthedUserCipherBundle
}

/**
 * Decrypt all the keys pulled down from the server and return the CryptoKeys needed
 * to interact with the app.
 */
export async function decryptCiphers(
  ciphers: Ciphers,
  stretchedMasterKey: CryptoKey,
): Promise<AuthedUserCipherBundle> {
  const userSymmetricKey = await decryptProtectedSymmetricKey(
    ciphers.protectedUserSymmetricKey,
    stretchedMasterKey,
  )
  const profileSymmetricKey = await decryptProtectedSymmetricKey(
    ciphers.protectedProfileSymmetricKey,
    userSymmetricKey,
  )
  const rsaPrivateKey = await decryptProtectedRSAPrivateKey(
    ciphers.protectedRsaPrivateKey,
    userSymmetricKey,
  )

  return {
    userSymmetricKey,
    profileSymmetricKey,
    rsaPrivateKey,
  } satisfies AuthedUserCipherBundle
}

/**
 * Given a username and password generate the master password hash that we send to
 * the server to login and download the encrypted bundle.
 */
export async function getMasterPasswordHash(
  username: string,
  password: string,
): Promise<Base64EncodedBinary> {
  const masterKey = await deriveMasterKey(username, password)

  const masterPasswordHash = await deriveMasterPasswordHash(masterKey, password)
  return arrayBufferToBase64(masterPasswordHash)
}

/**
 * Given a username and password derive the stretched master key
 */
export async function getStretchedKey(
  username: string,
  password: string,
): Promise<CryptoKey> {
  const masterKey = await deriveMasterKey(username, password)
  const stretchedMasterKey = await stretchMasterKey(masterKey)

  return stretchedMasterKey
}

async function deriveMasterKey(
  username: string,
  password: string,
): Promise<CryptoKey> {
  const enc = new TextEncoder()
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey", "deriveBits"],
  )

  const salt = enc.encode(username)

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 600_000,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true, // extractable
    ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  )
}

/**
 * Stretch the master key using HKDF.
 */
async function stretchMasterKey(masterKey: CryptoKey) {
  // TODO:
  return masterKey
  // const enc = new TextEncoder();
  // const salt = enc.encode(username);

  // return crypto.subtle.deriveKey(
  //   {
  //     name: "HKDF",
  //     salt,
  //     info: enc.encode(`stretched master key for ${username} on social signal`),
  //     hash: "SHA-256",
  //   },
  //   masterKey,
  //   { name: "AES-GCM", length: 512 },
  //   true,
  //   ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  // );
}

async function deriveMasterPasswordHash(
  masterKey: CryptoKey,
  masterPassword: string,
): Promise<ArrayBuffer> {
  const enc = new TextEncoder()
  const salt = enc.encode(masterPassword)

  // Export the master key as raw bytes and import as PBKDF2 key
  const masterKeyBytes = await crypto.subtle.exportKey("raw", masterKey)
  const pbkdf2Key = await crypto.subtle.importKey(
    "raw",
    masterKeyBytes,
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  )

  const buffer = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt,
      iterations: 1,
      hash: "SHA-256",
    },
    pbkdf2Key,
    256,
  )
  return buffer
}

/********************
 * UserSymmetricKey *
 ********************/

/**
 * Generate a new UserSymmetricKey for user data that is not shared.
 */
async function generateUserSymmetricKey(): Promise<CryptoKey> {
  return await crypto.subtle.importKey(
    "raw",
    (await generateNewAESKey(512)).slice(0, 32), // Use first 256 bits for AES-256
    { name: "AES-GCM" },
    true,
    ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  )
}

/********************
 * ProfileSymmetricKey *
 ********************/

/**
 * Generate a new ProfileSymmetricKey for profile data that can be shared.
 */
async function generateProfileSymmetricKey(): Promise<CryptoKey> {
  return await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  )
}

/********************
 *        RSA       *
 ********************/

/**
 * Generate RSA key pair for discovery and encrypted messaging.
 */
async function generateRsaKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 2048,
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  )
}

export async function encryptKeyWithRSAPublicKey(
  keyToEncrypt: CryptoKey,
  rsaPublicKey: CryptoKey,
): Promise<Base64EncodedBinary> {
  const keyBytes = await crypto.subtle.exportKey("raw", keyToEncrypt)
  const encryptedKey = await crypto.subtle.encrypt(
    {
      name: "RSA-OAEP",
    },
    rsaPublicKey,
    keyBytes,
  )
  return arrayBufferToBase64(encryptedKey)
}

export async function decryptKeyWithRSAPrivateKey(
  keyToDecrypt: Base64EncodedBinary,
  rsaPrivateKey: CryptoKey,
): Promise<CryptoKey> {
  const decryptedKeyBuf = await window.crypto.subtle.decrypt(
    {
      name: "RSA-OAEP",
    },
    rsaPrivateKey,
    base64ToArrayBuffer(keyToDecrypt),
  )
  return await window.crypto.subtle.importKey(
    "raw",
    decryptedKeyBuf,
    {
      name: "AES-GCM",
    },
    false,
    ["decrypt"],
  )
}

/********************
 *       Utils      *
 ********************/

async function generateNewAESKey(
  bitLength = 128 | 192 | 256 | 512,
): Promise<Uint8Array> {
  if (bitLength === 512) {
    // 512 bit keys are not supported in WebCrypto, so we concat two 256 bit keys
    const key1 = await generateNewAESKey(256)
    const key2 = await generateNewAESKey(256)
    return new Uint8Array([...key1, ...key2])
  }
  const aesParams = {
    name: "AES-CBC",
    length: bitLength,
  }

  const key = await window.crypto.subtle.generateKey(aesParams, true, [
    "encrypt",
    "decrypt",
  ])
  const rawKey = await window.crypto.subtle.exportKey("raw", key)
  return new Uint8Array(rawKey)
}

/**
 * Encrypt a key with another key using AES-GCM.
 * And prepare for export into a portable format.
 *
 * This will create a new initialization vector.
 */
async function encryptKeyWithKey(
  keyToEncrypt: CryptoKey,
  encryptionKey: CryptoKey,
  format: KeyFormat = "raw",
): Promise<Base64EncodedBinary> {
  const iv = crypto.getRandomValues(new Uint8Array(12)) // 12 bytes for AES-GCM
  const wrappedKey = await crypto.subtle.wrapKey(
    format,
    keyToEncrypt,
    encryptionKey,
    {
      name: "AES-GCM",
      iv: iv,
    },
  )
  const aesEncryptedBlob = {
    ciphertext: arrayBufferToBase64(wrappedKey),
    iv: arrayBufferToBase64(iv.buffer),
  } satisfies EncryptedBlobBase64
  return btoa(JSON.stringify(aesEncryptedBlob))
}

async function decryptProtectedSymmetricKey(
  protectedKey: Base64EncodedBinary,
  decryptionKey: CryptoKey,
): Promise<CryptoKey> {
  const parsedEncryptedBlob = await EncryptedBlobBase64Schema.parseAsync(
    JSON.parse(atob(protectedKey)),
  )
  const ciphertext = base64ToArrayBuffer(parsedEncryptedBlob.ciphertext)
  const iv = base64ToArrayBuffer(parsedEncryptedBlob.iv)

  return await crypto.subtle.unwrapKey(
    "raw",
    ciphertext,
    decryptionKey,
    {
      name: "AES-GCM",
      iv: iv,
    },
    "AES-GCM",
    true,
    ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  )
}

/**
 * Decrypt a protected RSA private key.
 */
async function decryptProtectedRSAPrivateKey(
  protectedRsaPrivateKey: Base64EncodedBinary,
  decryptionKey: CryptoKey,
): Promise<CryptoKey> {
  const parsedEncryptedBlob = await EncryptedBlobBase64Schema.parseAsync(
    JSON.parse(atob(protectedRsaPrivateKey)),
  )
  const ciphertext = base64ToArrayBuffer(parsedEncryptedBlob.ciphertext)
  const iv = base64ToArrayBuffer(parsedEncryptedBlob.iv)

  return await crypto.subtle.unwrapKey(
    "pkcs8",
    ciphertext,
    decryptionKey,
    {
      name: "AES-GCM",
      iv: iv,
    },
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["decrypt", "unwrapKey"],
  )
}

/**
 * Encrypt data with a symmetric key using AES-GCM.
 *
 * This should almost always be encoded with the UserSymmetricKey if it is private
 * to the user. And if it is data that is intended to be shared it should be encoded
 * with the ProfileSymmetricKey.
 */
export async function encryptWithSymmetricKey(
  data: BufferSource,
  key: CryptoKey,
): Promise<Base64EncodedBinary> {
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const ciphertext = await window.crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    key,
    data,
  )

  const aesEncryptedBlob = {
    ciphertext: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv.buffer),
  } satisfies EncryptedBlobBase64
  return btoa(JSON.stringify(aesEncryptedBlob))
}

/**
 * Decrypt data with a symmetric key using AES-GCM.
 *
 * This should almost always be decoded with the UserSymmetricKey if it is private
 * to the user. And if it is data that is intended to be shared it should be decoded
 * with the ProfileSymmetricKey.
 */
export async function decryptWithSymmetricKey(
  data: Base64EncodedBinary,
  decryptionKey: CryptoKey,
): Promise<ArrayBuffer> {
  const parsedEncryptedBlob = await EncryptedBlobBase64Schema.parseAsync(
    JSON.parse(atob(data)),
  )
  const ciphertext = base64ToArrayBuffer(parsedEncryptedBlob.ciphertext)
  const iv = base64ToArrayBuffer(parsedEncryptedBlob.iv)

  return await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    decryptionKey,
    ciphertext,
  )
}

async function exportRSAPublicKey(
  rsaPublicKey: CryptoKey,
): Promise<Base64EncodedBinary> {
  return arrayBufferToBase64(
    await crypto.subtle.exportKey("spki", rsaPublicKey),
  )
}

export async function importRSAPublicKey(
  rsaPublicKeyRaw: Base64EncodedBinary,
): Promise<CryptoKey> {
  return await crypto.subtle.importKey(
    "spki",
    base64ToArrayBuffer(rsaPublicKeyRaw),
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    false,
    ["encrypt"],
  )
}

export async function exportStretchedMasterKey(
  stretchedMasterKey: CryptoKey,
): Promise<Base64EncodedBinary> {
  const masterKeyBytes = await crypto.subtle.exportKey(
    "raw",
    stretchedMasterKey,
  )
  return fromBufferToBase64(masterKeyBytes)
}

export async function importStretchedMasterKey(
  stretchedMasterKeyBytes: Base64EncodedBinary,
): Promise<CryptoKey> {
  return await crypto.subtle.importKey(
    "raw",
    base64ToArrayBuffer(stretchedMasterKeyBytes),
    { name: "AES-GCM", length: 256 },
    true, // extractable
    ["encrypt", "decrypt", "wrapKey", "unwrapKey"],
  )
}
